import { useGetStateItem } from './casper-service-hooks';
import {
  findHash,
  hasAccountHashPrefix,
  isValidAccountHash,
  isValidCsprName,
  isValidPublicKey,
} from '../utils/account';
import {
  useGetAccountInfo,
  useGetBlock,
  useGetBlockByHeight,
  useGetCentralizedAccountInfo,
  useGetContractPackages,
  useGetCsprNameInfo,
  useGetDeploy,
  useGetPaginatedAccountInfo,
} from './event-store-hooks';
import { NamedKeyPrefix } from '@make-software/cspr-design';
import { ContractDataRawResult } from '@block-explorer/entities';
import { checkApiHookIsLoading } from './api-hooks-utils';
import { useSelector } from 'react-redux';
import { RootState } from 'typesafe-actions';
import {
  canSearchByNameTerm,
  hashPrefixRegEx,
  isBlockHeightTerm,
  resolveSearchResult,
  SearchByNameResults,
  SearchResult,
} from './search-utils';
import { useDebounce } from './use-debounce';
import { hasContractHashPrefix } from '../utils/contract';
import { getHashWithoutPrefix } from '../utils/hash';

export type SearchReturnType = {
  loading: boolean;
  error: string | null;
  data: SearchResult | null;
};

export type SearchByNameReturnType = {
  loading: boolean;
  error: string | null;
  data: SearchByNameResults | null;
};

export const useSearchByName = (
  searchTerm: string | null
): SearchByNameReturnType => {
  const debouncedSearchTerm = useDebounce(searchTerm);
  const canSearchByName = canSearchByNameTerm(searchTerm);
  const isDebouncing = canSearchByName && debouncedSearchTerm !== searchTerm;
  const requestSearchTerm =
    !isDebouncing && canSearchByNameTerm(debouncedSearchTerm)
      ? debouncedSearchTerm
      : null;

  const {
    data: contractPackages,
    loading: contractPackagesLoading,
    error: contractPackagesError,
  } = useGetContractPackages(
    requestSearchTerm
      ? {
          search: requestSearchTerm,
          includes:
            'account_info,centralized_account_info,activity_num(30),cspr_name',
          page: 1,
          page_size: 250,
        }
      : null
  );

  const {
    data: verifiedAccounts,
    loading: verifiedAccountsLoading,
    error: verifiedAccountsError,
  } = useGetPaginatedAccountInfo(
    requestSearchTerm
      ? {
          search: requestSearchTerm,
          page: 1,
          page_size: 250,
        }
      : null
  );

  const {
    data: centralizedAccounts,
    loading: centralizedAccountsLoading,
    error: centralizedAccountsError,
  } = useGetCentralizedAccountInfo(
    requestSearchTerm
      ? {
          search: requestSearchTerm,
        }
      : null
  );

  const contractPackagesHookLoading = checkApiHookIsLoading({
    loading: contractPackagesLoading,
    error: contractPackagesError,
  });

  const verifiedAccountsHookLoading = checkApiHookIsLoading({
    loading: verifiedAccountsLoading,
    error: verifiedAccountsError,
  });

  const centralizedAccountsHookLoading = checkApiHookIsLoading({
    loading: centralizedAccountsLoading,
    error: centralizedAccountsError,
  });

  const searchByNameRequestLoading =
    requestSearchTerm != null &&
    (contractPackages == null ||
      verifiedAccounts == null ||
      centralizedAccounts == null);

  const loading =
    isDebouncing ||
    searchByNameRequestLoading ||
    contractPackagesHookLoading ||
    verifiedAccountsHookLoading ||
    centralizedAccountsHookLoading;

  if (!canSearchByName) {
    return {
      loading: false,
      error: null,
      data: null,
    };
  }

  if (loading) {
    return {
      loading: true,
      error: null,
      data: null,
    };
  }

  return {
    loading: false,
    error:
      contractPackagesError ||
      verifiedAccountsError ||
      centralizedAccountsError,
    data: {
      contractPackages,
      verifiedAccounts,
      centralizedAccounts,
    },
  };
};

/**
 * @usage
 * ```
 * const { data: { type, hash, results } } = useSearch('hash');
 * ```
 * @todo consider moving out of event-store-utils
 */
export const useSearch = (rawSearchTerm: string | null): SearchReturnType => {
  const searchTerm = rawSearchTerm?.trim().toLowerCase() || null;
  const searchTermLength = searchTerm?.trim().length;
  const blockHeightSearchTerm =
    searchTerm && searchTermLength < 64 && isBlockHeightTerm(searchTerm)
      ? searchTerm.replace(/,/g, '').trim()
      : null;

  const { selectedCurrency: currency } = useSelector(
    (state: RootState) => state.currencies
  );

  const hasStateItemSearchPrefix =
    hasContractHashPrefix(searchTerm) || hasAccountHashPrefix(searchTerm);
  const stateItemSearchHash =
    searchTerm && hasStateItemSearchPrefix
      ? getHashWithoutPrefix(searchTerm)
      : searchTerm;
  const stateItemKeyPrefix = hasAccountHashPrefix(searchTerm)
    ? NamedKeyPrefix.ACCOUNT_HASH
    : NamedKeyPrefix.HASH;

  const searchTermWithPrefix =
    stateItemSearchHash?.length === 64
      ? stateItemKeyPrefix + stateItemSearchHash
      : searchTerm;

  const {
    data: blockByHeight,
    loading: blockByHeightLoading,
    error: blockByHeightError,
  } = useGetBlockByHeight(
    blockHeightSearchTerm ? { block_identifier: blockHeightSearchTerm } : null
  );

  const {
    data: stateItemData,
    error: stateItemError,
    loading: stateItemLoading,
  } = useGetStateItem<ContractDataRawResult | null>(
    searchTermWithPrefix && findHash(searchTermWithPrefix)
      ? {
          key: searchTermWithPrefix,
        }
      : null
  );

  const { data: accountPublicKey, loading: accountPublicKeyLoading } =
    useGetAccountInfo(
      searchTerm && searchTermLength === 64 ? { accountHash: searchTerm } : null
    );

  const {
    data: blockData,
    error: blockError,
    loading: blockLoading,
  } = useGetBlock(searchTerm && searchTermLength === 64 ? searchTerm : null);

  const {
    data: deployData,
    error: deployError,
    loading: deployLoading,
  } = useGetDeploy(searchTerm && searchTermLength === 64 ? searchTerm : null, {
    includes: `rate(${currency.id}),contract_entrypoint,contract_package,transfers,entry_point_id,nft_token_actions,ft_token_actions`,
  });

  const isCsprNameValid = isValidCsprName(searchTerm || '');

  const {
    data: csprNameInfo,
    loading: csprNameInfoLoading,
    error: csprNameInfoError,
  } = useGetCsprNameInfo(
    searchTerm && isCsprNameValid
      ? {
          name: searchTerm,
          includes: 'resolved_public_key,account_info,centralized_account_info',
        }
      : null
  );

  const {
    data: searchByNameResults,
    loading: searchByNameResultsLoading,
    error: searchByNameResultsError,
  } = useSearchByName(searchTerm);

  if (searchTerm == null) {
    return { loading: false, error: null, data: null };
  }
  const [searchTermWithoutPrefix] = searchTerm
    .replace(hashPrefixRegEx, '')
    .split('-');

  const isValidAccount = isValidPublicKey(searchTermWithoutPrefix);
  const isAccountHashValid = isValidAccountHash(searchTermWithoutPrefix);

  const isValidSearchTermLength =
    (searchTermWithoutPrefix && searchTermWithoutPrefix.length === 64) ||
    isCsprNameValid ||
    isValidAccount ||
    canSearchByNameTerm(searchTerm) ||
    blockHeightSearchTerm != null;

  if (!isValidSearchTermLength) {
    return {
      loading: false,
      error: 'Search could not complete.',
      data: null,
    };
  }

  const deployHookLoading = checkApiHookIsLoading({
    loading: deployLoading,
    error: deployError,
  });

  const blockHookLoading = checkApiHookIsLoading({
    loading: blockLoading,
    error: blockError,
  });

  const accountHookLoading = checkApiHookIsLoading({
    loading: accountPublicKeyLoading,
    error: accountPublicKey?.error?.message || null,
  });

  const csprNameInfoHookLoading = checkApiHookIsLoading({
    loading: csprNameInfoLoading,
    error: csprNameInfoError,
  });

  const stateHookLoading = checkApiHookIsLoading({
    loading: stateItemLoading,
    error: stateItemError,
  });

  const blockByHeightHookLoading = checkApiHookIsLoading({
    loading: blockByHeightLoading,
    error: blockByHeightError,
  });

  const blockByHeightRequestLoading =
    blockHeightSearchTerm != null &&
    blockByHeight == null &&
    blockByHeightError == null;

  if (
    isCsprNameValid &&
    (csprNameInfoHookLoading ||
      (csprNameInfo == null && csprNameInfoError == null))
  ) {
    return { loading: true, error: null, data: null };
  }

  if (
    !isValidAccount &&
    !isCsprNameValid && // workaround for account loading case
    (deployHookLoading ||
      blockHookLoading ||
      accountHookLoading ||
      csprNameInfoHookLoading ||
      stateHookLoading ||
      blockByHeightHookLoading ||
      blockByHeightRequestLoading ||
      searchByNameResultsLoading)
  ) {
    return { loading: true, error: null, data: null };
  }

  const loadedData =
    deployData ||
    blockData ||
    blockByHeight?.data ||
    stateItemData ||
    csprNameInfo?.data ||
    searchByNameResults;

  if (
    !isValidAccount &&
    !isAccountHashValid &&
    !loadedData &&
    (csprNameInfoError ||
      deployError ||
      blockError ||
      blockByHeightError ||
      stateItemError ||
      accountPublicKey?.error ||
      searchByNameResultsError)
  ) {
    return {
      loading: false,
      error: 'Search could not complete.',
      data: null,
    };
  }

  const response = resolveSearchResult({
    searchTerm,
    isValidAccount,
    csprNameInfo,
    blockData,
    deployData,
    accountPublicKey,
    stateItemData,
    searchByNameResults,
    blockByHeight,
  });

  if (!response) {
    return {
      loading: false,
      error: 'Search could not complete.',
      data: null,
    };
  }

  return {
    loading: false,
    error: null,
    data: response,
  };
};
